import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button, Modal, Space, Typography } from 'antd';
import {
  updateCurrentUser,
  updateIsLoggedIn,
  updatePlanPermissions,
  updateRmDetails,
  updateNavigationTourGuide,
  updateProductList,
  updateExtraParam,
  updateTheme
} from 'store/user';
import Alert from 'components/Alert';
import handleApiExceptions from 'utils/handleApiExceptions';
import PageLoader from 'components/PageLoader';
import User from 'services/authentication';
import merchantAPI from 'services/merchants';
import trackingService from 'services/trackingService';
import handleCommonRediection from 'components/CommonRedirect.js/CommonRediection';
import PORTAL_THEME from '../theme';

const ModalWrapper = styled(Modal)`
  .ant-modal-close {
    top: 10px;
    right: 10px;
  }
  .ant-modal-close-x {
    font-size: 16px;
  }
`;

function RequireAuth() {
  // state to store whether the auth check has completed.
  // const [loading, setLoading] = useState(true);
  const [userConsentModal, setUserConsentModal] = useState(false);
  // state to store whether the user is authenticated
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);

  const user = useSelector((state) => state.user.currentUser);
  const configs = useSelector((state) => state.user.currentUser.configuration);
  const permissions = useSelector((state) => state.user.planPermissions);
  const indexPage = useSelector((state) => state.user.currentUser?.index_page);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const fetchUserInfo = () => {
    User.info()
      .then((response) => {
        if (response?.status === 403) {
          Alert('Alert!', `${response?.data?.message}`, 'warning', {
            okText: 'Go to Easebuzz',
            onOk: () => {
              window.location.replace(`${process.env.REACT_APP_DASHBOARD_URL}`);
            }
          });
        }
        if (response.success) {
          if (!response.data.configuration.permission_configs.portal_access) {
            handleCommonRediection(response.data.user_id);
          } else {
            dispatch(
              updateIsLoggedIn(response.data.configuration.permission_configs.portal_access)
            );
            dispatch(updateCurrentUser(response.data));
            dispatch(updateExtraParam(response.data.configuration.extra_param));
            dispatch(
              updateTheme(PORTAL_THEME[response?.data?.merchant_id] || PORTAL_THEME.default)
            );
            dispatch(updatePlanPermissions(response.data.configuration.permission_configs));
            dispatch(updateProductList(response.data.active_product_list));
            setUserConsentModal(response.data.is_consent);
            trackingService.identifyUser(response.data?.merchant_id); // eslint-disable-line no-underscore-dangle
          }
        } else {
          handleApiExceptions(response);
        }
      })
      .catch((error) => {
        handleApiExceptions({
          success: false,
          status: (error.response && error.response.status) || 400
        });
      });
  };

  const updateConsent = async () => {
    const response = await merchantAPI.updateConsent();

    if (!response.success) {
      console.log('something went wrong');
      // Alert('something went wrong', 'Please contact support team for this', 'error');
    }
  };

  const handleUserConsentModal = (e) => {
    setUserConsentModal(e);
  };

  const handleAgree = () => {
    handleUserConsentModal(false);
    updateConsent();
  };

  //  handle the index page routes
  const handleIndexRoutes = () => {
    const filteredUsers = indexPage;
    if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_dashboard') &&
      permissions?.can_view_dashboard === true &&
      filteredUsers?.can_view_dashboard === true
    ) {
      navigate('/dashboard');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_referrals') &&
      permissions?.can_view_referrals === true &&
      filteredUsers?.can_view_referrals === true
    ) {
      navigate('/referrals');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_leads') &&
      permissions?.can_view_leads === true &&
      filteredUsers?.can_view_leads === true
    ) {
      navigate('/leads');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_gtv_dashboard_report') &&
      permissions?.can_view_gtv_dashboard_report === true &&
      filteredUsers?.can_view_gtv_dashboard_report === true
    ) {
      navigate('/referrals/referral-gtv');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_transactions') &&
      permissions?.can_view_transactions === true &&
      filteredUsers?.can_view_transactions === true
    ) {
      navigate('/transactions');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_custom_report') &&
      permissions?.can_view_custom_report === true &&
      filteredUsers?.can_view_custom_report === true
    ) {
      navigate('/custom-reports');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_payout_report') &&
      permissions?.can_view_payout_report === true &&
      filteredUsers?.can_view_payout_report === true
    ) {
      navigate('/payout-reports');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_invoices') &&
      permissions?.can_view_invoices === true &&
      filteredUsers?.can_view_invoices === true
    ) {
      navigate('/invoices');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_settings') &&
      permissions?.can_view_settings === true &&
      filteredUsers?.can_view_settings === true
    ) {
      navigate('/settings');
    } else if (location.pathname === '/teams') {
      navigate('/');
    } else if (location.pathname === '/' && !indexPage) {
      navigate('/no-permission');
    } else if (
      location.pathname === '/' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_teams') &&
      permissions?.can_view_teams === true &&
      filteredUsers?.can_view_teams === true
    ) {
      navigate('/teams');
    }
  };

  //  handle the routing basis on the pathname
  const handleRouting = () => {
    if (location.pathname === '/' && Object.keys(indexPage).length === 0) {
      navigate('/no-permission');
    }
    if (location.pathname === '/' && indexPage) {
      handleIndexRoutes();
    }
    if (
      location.pathname === '/referrals' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_referrals') &&
      permissions?.can_view_referrals === false
    ) {
      handleIndexRoutes();
    }
    if (
      location.pathname === '/dashboard' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_dashboard') &&
      permissions?.can_view_dashboard === false
    ) {
      handleIndexRoutes();
    }
    if (
      location.pathname === '/transactions' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_transactions') &&
      permissions?.can_view_transactions === false
    ) {
      handleIndexRoutes();
    }

    if (
      (location.pathname === '/leads' ||
        location.pathname === '/leads/create' ||
        location.pathname === '/leads/update') &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_leads') &&
      permissions?.can_view_leads === false
    ) {
      handleIndexRoutes();
    }

    if (
      location.pathname === '/invoices' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_invoices') &&
      permissions?.can_view_invoices === false
    ) {
      handleIndexRoutes();
    }

    if (
      location.pathname === '/payout-reports' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_payout_report') &&
      permissions?.can_view_payout_report === false
    ) {
      handleIndexRoutes();
    }

    if (
      location.pathname === '/custom-reports' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_custom_report') &&
      permissions?.can_view_custom_report === false
    ) {
      handleIndexRoutes();
    }

    if (
      location.pathname === '/settings' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_settings') &&
      permissions?.can_view_settings === false
    ) {
      handleIndexRoutes();
    }

    if (
      location.pathname === '/teams' &&
      Object.prototype.hasOwnProperty.call(permissions, 'can_view_teams') &&
      permissions?.can_view_teams === false
    ) {
      handleIndexRoutes();
    }
  };

  useEffect(() => {
    fetchUserInfo();
  }, []);

  useEffect(() => {
    handleUserConsentModal(true);
  }, [user]);
  useEffect(() => {
    if (isLoggedIn) {
      if (permissions !== undefined) {
        handleRouting();
      }
    }
  }, [isLoggedIn, configs, permissions]);

  useEffect(() => {
    if (isLoggedIn && location.pathname === '/') {
      handleRouting();
    }
  }, [location.pathname]);

  const fetchRmDetails = async () => {
    const response = await merchantAPI.fetchRmMappingDetails();
    if (response.success) {
      dispatch(updateRmDetails(response?.data));
    } else {
      console.log('something went wrong');
    }
  };
  const ReferralSummary = async () => {
    await merchantAPI.PartnerAggregatedSummary();
  };

  const fetchNavigation = async () => {
    const response = await merchantAPI.fetchNavigationFlag();
    if (response.success) {
      dispatch(updateNavigationTourGuide(response?.data));
    } else {
      console.log('something went wrong');
    }
  };

  useEffect(() => {
    if (isLoggedIn && configs && permissions) {
      fetchRmDetails();
      fetchNavigation();
      ReferralSummary();
    }
  }, [isLoggedIn, configs, permissions]);

  if (isLoggedIn) {
    // if (
    //   configs !== undefined &&
    //   !configs.permission_configs.can_view_referrals &&
    //   !configs.permission_configs.can_view_leads &&
    //   !configs.permission_configs.can_view_invoices &&
    //   !configs.permission_configs.can_view_payout_report &&
    //   !configs.permission_configs.can_view_custom_report &&
    //   !configs.permission_configs.can_view_settings
    // ) {
    //   Alert('Alert!', `${error.response.data.message}`, 'warning', {
    //     okText: 'Go to Easebuzz',
    //     onOk: () => {
    //       window.location.replace(`${process.env.REACT_APP_DASHBOARD_URL}`);
    //     }
    //   });
    //   setTimeout(() => {
    //     window.location.replace(`${process.env.REACT_APP_DASHBOARD_URL}`);
    //   }, 5000);

    //   return null;
    // }

    // This is the view if the user was authenticated.
    if (user !== undefined && user.is_consent === false) {
      return (
        <>
          <ModalWrapper
            title={
              <Typography.Text
                style={{
                  fontSize: '20px',
                  fontWeight: '500',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '10px',
                  borderBottom: '1px solid #A0A0A0'
                }}
              >
                User Consent - Partner&apos;s Terms of Use
              </Typography.Text>
            }
            centered
            open={userConsentModal}
            closable={false}
            footer={[
              <Space style={{ width: '100%', justifyContent: 'center' }} key="consent_action">
                <Button type="primary" onClick={handleAgree} size="large">
                  I Agree
                </Button>
              </Space>
            ]}
          >
            <Space
              style={{
                padding: '25px',
                textAlign: 'center'
              }}
            >
              <Typography.Text
                style={{
                  fontSize: '16px',
                  width: '100%'
                }}
              >
                By accessing this Partner&apos;s application and services, I agree to the{' '}
                <a
                  href={`${process.env.REACT_APP_EBZ_URL}terms/partner`}
                  style={{ textDecoration: 'underline' }}
                  target="_blank"
                  rel="noreferrer"
                >
                  Partner&apos;s Terms of Use.
                </a>
              </Typography.Text>
            </Space>
          </ModalWrapper>
          <Outlet />
        </>
      );
    }
    return <Outlet />;
  }

  // This is the view if the user was not authenticated
  // most likely not be displayed for any significant (or even preceivable)
  // amount of time.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        alignItems: 'center',
        width: '100vw',
        height: '100vh'
      }}
    >
      <PageLoader />
    </div>
  );
}

export default RequireAuth;
